/**
 * Sponsor profile model types.
 * 
 * Elm-ish architecture: Model is pure data, no logic.
 * State machine for sponsor profile CRUD.
 * 
 * Profile activation per REQ-125, REQ-126, REQ-127:
 * - Gender must be selected first before goal
 * - Goal is only available after gender is set
 * - Age validation: 18-120
 */
import type { SponsorProfile, Gender, SponsorGoal } from '@/api/profiles';

/**
 * Profile form data for create/update including activation fields.
 */
export interface ProfileFormData {
  /** Gender selection per REQ-126 (must be chosen first) */
  gender?: Gender;
  /** Goal selection per REQ-127 (available after gender) */
  goal?: SponsorGoal;
  /** Age per REQ-125 (must be 18-120) */
  age?: number;
  display_name: string;
  headline: string;
  bio: string;
  city_or_region: string;
}

/**
 * Sponsor profile program state.
 */
export type SponsorProfileStatus =
  | 'idle'              // Initial state, not loaded
  | 'loading'           // Loading existing profile
  | 'ready'             // Profile loaded, can edit
  | 'saving'            // Saving changes
  | 'saved'             // Save succeeded
  | 'active'            // Profile is now active (all required fields complete)
  | 'error'             // Error state
  | 'empty';            // No profile exists yet

/**
 * Sponsor profile model state.
 */
export interface SponsorProfileModel {
  /** Current state machine status */
  status: SponsorProfileStatus;
  
  /** Existing profile data (null if no profile exists) */
  profile: SponsorProfile | null;
  
  /** Current form values for editing */
  form: ProfileFormData;
  
  /** Error message if status is 'error' */
  error: string | null;
  
  /** Pending action for loading indicator */
  pendingAction: SponsorProfileAction | null;
  
  /** Profile status from API per REQ-125 */
  profileStatus: 'inactive' | 'active' | 'blocked' | 'deleted';
  
  /** Missing activation fields from API per REQ-125 */
  missingActivationFields: readonly string[];
  
  /** Whether goal can be selected (requires gender first per REQ-126) */
  canSelectGoal: boolean;
}

/**
 * Actions that can be performed on sponsor profile.
 */
export type SponsorProfileAction =
  | 'LoadProfile'
  | 'StartEdit'
  | 'UpdateForm'
  | 'SaveProfile'
  | 'CancelEdit'
  | 'Retry';

/**
 * Initial model state.
 */
export function initialModel(): SponsorProfileModel {
  return {
    status: 'idle',
    profile: null,
    form: {
      gender: undefined,
      goal: undefined,
      age: undefined,
      display_name: '',
      headline: '',
      bio: '',
      city_or_region: '',
    },
    error: null,
    pendingAction: null,
    profileStatus: 'inactive',
    missingActivationFields: [],
    canSelectGoal: false,
  };
}

/**
 * Profile form validation errors.
 */
export interface ProfileFormErrors {
  gender?: string;
  goal?: string;
  age?: string;
  display_name?: string;
  headline?: string;
  bio?: string;
  city_or_region?: string;
}

/**
 * Validate profile form data.
 * Returns empty object if valid, otherwise field-specific errors.
 */
export function validateForm(form: ProfileFormData): ProfileFormErrors {
  const errors: ProfileFormErrors = {};
  
  if (form.display_name && [...form.display_name].length > 100) {
    errors.display_name = 'Display name must be 100 characters or less';
  }
  
  if (form.headline && [...form.headline].length > 200) {
    errors.headline = 'Headline must be 200 characters or less';
  }
  
  if (form.bio && [...form.bio].length > 1000) {
    errors.bio = 'Bio must be 1000 characters or less';
  }
  
  if (form.city_or_region && [...form.city_or_region].length > 100) {
    errors.city_or_region = 'City/region must be 100 characters or less';
  }
  
  return errors;
}

/**
 * Check if goal can be selected based on gender.
 * Per REQ-126, goal requires gender to be selected first.
 */
export function canSelectGoal(form: ProfileFormData): boolean {
  return form.gender !== undefined;
}

/**
 * Map missing activation field codes to user-friendly labels.
 * Per REQ-125 for frontend UX.
 */
export function fieldCodeToLabel(code: string): string {
  const fieldLabels: Record<string, string> = {
    'gender': 'Gender',
    'goal': 'Goal',
    'age': 'Age',
    'city': 'City or region',
    'display_name': 'Display name',
    'headline_or_bio': 'Headline or bio',
    'headline': 'Headline',
    'bio': 'Bio',
  };
  return fieldLabels[code] || code;
}

/**
 * Convert API profile to form data including activation fields.
 */
export function profileToForm(profile: SponsorProfile): ProfileFormData {
  return {
    gender: profile.gender,
    goal: profile.goal,
    age: profile.age,
    display_name: profile.display_name || '',
    headline: profile.headline || '',
    bio: profile.bio || '',
    city_or_region: profile.city_or_region || '',
  };
}

/**
 * Convert form data to API update payload including activation fields.
 * Per REQ-126, goal should only be sent if gender is already set.
 */
export function formToUpdatePayload(form: ProfileFormData): Omit<import('@/api/profiles').UpdateSponsorProfileRequest, 'profile_type'> {
  return {
    gender: form.gender,
    // Only include goal if gender is set (per REQ-126 gender-first rule)
    goal: form.gender ? form.goal : undefined,
    age: form.age,
    display_name: form.display_name || undefined,
    headline: form.headline || undefined,
    bio: form.bio || undefined,
    city_or_region: form.city_or_region || undefined,
  };
}
